import { useCallback, useState } from 'react';

import { AuthRedirectError } from '@/api/useAuthedClient';
import { RestError, useAuthedRest } from '@/api/useAuthedRest';

import { useGraphQLQuery } from './useGraphQLQuery';

// Mirrors the green-coffee admin API in
// apps/api-cf/src/backstage/sourcing/stages/review. Timestamps arrive as ISO
// strings over JSON.
export type GreenCoffeeGrade = 'good' | 'bad';
// `dlq` is the ungraded rows with no country: the server refuses to publish
// them, so they sit outside the pending queue. `published` is every row with a
// coffee, whether the judge or a human Good published it; `pending` holds the
// judge's FAIL rows and the rows it has not judged.
export type GreenCoffeeStatus = 'pending' | 'dlq' | 'published' | 'bad';

export interface GreenCoffeeFields {
  name: string;
  isBlend: boolean;
  farm: string | null;
  region: string | null;
  countryCode: string | null;
  processingType: string | null;
  varieties: string[];
  tastingNotes: string[];
}

export type GreenCoffeeFieldKey = keyof GreenCoffeeFields;

export interface GreenCoffeeSource {
  title: string;
  feedDescription: string | null;
  pageDescription: string | null;
}

// The LLM judge's verdict, stored apart from the human grade. `rule` is the
// rubric rule a FAIL broke, 0 on a PASS.
export interface GreenCoffeeJudge {
  verdict: 'PASS' | 'FAIL';
  rule: number;
  reason: string;
  promptHash: string;
  judgedAt: string;
}

export interface GreenCoffee {
  id: string;
  listingId: string;
  roasterId: string;
  sourceHash: string;
  isUpdate: boolean;
  isBlend: boolean;
  fields: GreenCoffeeFields;
  source: GreenCoffeeSource;
  grade: GreenCoffeeGrade | null;
  notes: string | null;
  gradedAt: string | null;
  publishedCoffeeId: string | null;
  judge: GreenCoffeeJudge | null;
  // The fields an admin changed before publishing; null unless corrected.
  correctedFields: GreenCoffeeFieldKey[] | null;
  createdAt: string;
}

export interface GreenCoffeeCounts {
  pending: number;
  dlq: number;
  published: number;
  bad: number;
}

// Only the list endpoint carries the listing's image; getById/grade don't.
export interface GreenCoffeeListItem extends GreenCoffee {
  imageUrl: string | null;
}

interface UseGreenCoffeesReturn {
  greenCoffees: GreenCoffeeListItem[] | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<GreenCoffeeListItem[] | null>;
}

// One status bucket: pending is oldest-first, the rest newest-first (server-side).
export function useGreenCoffees(status: GreenCoffeeStatus): UseGreenCoffeesReturn {
  const rest = useAuthedRest();
  const { data, isLoading, error, refetch } = useGraphQLQuery<GreenCoffeeListItem[]>({
    fetcher: (signal) =>
      rest.request<GreenCoffeeListItem[]>(`/admin/green-coffee?status=${status}`, { signal }),
    deps: [status],
  });
  return { greenCoffees: data, isLoading, error, refetch };
}

interface UseGreenCoffeeCountsReturn {
  counts: GreenCoffeeCounts | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<GreenCoffeeCounts | null>;
}

export function useGreenCoffeeCounts(enabled = true): UseGreenCoffeeCountsReturn {
  const rest = useAuthedRest();
  const { data, isLoading, error, refetch } = useGraphQLQuery<GreenCoffeeCounts>({
    fetcher: (signal) => rest.request<GreenCoffeeCounts>('/admin/green-coffee/counts', { signal }),
    deps: [],
    enabled,
  });
  return { counts: data, isLoading, error, refetch };
}

// `message: null` means an Auth0 redirect is in flight — render no error.
export type GradeGreenCoffeeResult =
  { ok: true; greenCoffee: GreenCoffee } | { ok: false; message: string | null };

interface UseGradeGreenCoffeeReturn {
  grade: (
    id: string,
    grade: GreenCoffeeGrade,
    notes?: string | null,
  ) => Promise<GradeGreenCoffeeResult>;
  isSaving: boolean;
}

const GENERIC_GRADE_ERROR = "Couldn't save the grade — try again";
const GENERIC_CORRECT_ERROR = "Couldn't publish the correction — try again";

function failure(err: unknown, generic: string): GradeGreenCoffeeResult {
  if (err instanceof AuthRedirectError) return { ok: false, message: null };
  if (err instanceof RestError && (err.status === 400 || err.status === 404)) {
    return { ok: false, message: err.message || generic };
  }
  return { ok: false, message: generic };
}

export function useGradeGreenCoffee(): UseGradeGreenCoffeeReturn {
  const rest = useAuthedRest();
  const [isSaving, setIsSaving] = useState(false);

  const grade = useCallback(
    async (
      id: string,
      value: GreenCoffeeGrade,
      notes?: string | null,
    ): Promise<GradeGreenCoffeeResult> => {
      setIsSaving(true);
      // The endpoint rejects unknown keys and an empty-string note, so an
      // absent note means omitting the key entirely.
      const trimmed = notes?.trim();
      const body = trimmed ? { grade: value, notes: trimmed } : { grade: value };
      try {
        const greenCoffee = await rest.request<GreenCoffee>(
          `/admin/green-coffee/${encodeURIComponent(id)}/grade`,
          { method: 'POST', body: JSON.stringify(body) },
        );
        return { ok: true, greenCoffee };
      } catch (err) {
        return failure(err, GENERIC_GRADE_ERROR);
      } finally {
        setIsSaving(false);
      }
    },
    [rest],
  );

  return { grade, isSaving };
}

interface UseCorrectGreenCoffeeReturn {
  correct: (id: string, fields: GreenCoffeeFields) => Promise<GradeGreenCoffeeResult>;
  isSaving: boolean;
}

// Sends all eight fields; the server diffs them against the stored row.
export function useCorrectGreenCoffee(): UseCorrectGreenCoffeeReturn {
  const rest = useAuthedRest();
  const [isSaving, setIsSaving] = useState(false);

  const correct = useCallback(
    async (id: string, fields: GreenCoffeeFields): Promise<GradeGreenCoffeeResult> => {
      setIsSaving(true);
      try {
        const greenCoffee = await rest.request<GreenCoffee>(
          `/admin/green-coffee/${encodeURIComponent(id)}/correct`,
          { method: 'POST', body: JSON.stringify({ fields }) },
        );
        return { ok: true, greenCoffee };
      } catch (err) {
        return failure(err, GENERIC_CORRECT_ERROR);
      } finally {
        setIsSaving(false);
      }
    },
    [rest],
  );

  return { correct, isSaving };
}
